/**
 * Pure helpers for the Command Center page (no React, no I/O). What needs
 * attention is decided in the backend (control-tower api/command-center); this
 * only groups and formats the stored snapshot.
 */
import { DEFAULT_RISK_BANDS, fmtMoney, fmtUnits } from "./inventoryRiskPageDefaults";

export { fmtMoney, fmtUnits };

export const SEVERITY_LABELS = { today: "Act today", week: "This week" };

// The three lanes, left to right: what runs out, who is waiting, what is late.
export const SIGNALS = [
  { key: "stock", label: "Stock running out", noun: "product" },
  { key: "orders", label: "Customer orders", noun: "group" },
  { key: "po", label: "Purchase orders", noun: "PO" },
];

export const SIGNAL_BY_KEY = Object.fromEntries(SIGNALS.map((signal) => [signal.key, signal]));

export const RISK_LABELS = {
  out_of_stock: "Out of stock",
  critical: "Critical",
  at_risk: "At risk",
};

export const COVERAGE_LABELS = {
  none: "Nothing on order",
  unconfirmed: "Stale PO only",
  late: "PO arrives too late",
  pending: "PO overdue / undated",
  covered: "Covered",
};

/** $12,598 → "$12.6k", $3,853 → "$3.9k", $250,400 → "$250k"; under $1,000 stays exact. */
export const fmtCompactMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  if (Math.abs(number) < 1000) return fmtMoney(number);
  if (Math.abs(number) < 1000000) {
    return `$${(number / 1000).toFixed(Math.abs(number) < 100000 ? 1 : 0).replace(/\.0$/, "")}k`;
  }
  return `$${(number / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
};

// Snapshots built before the 30-day projection carry only a daily rate.
const hasWindowLoss = (item) => item?.impact?.lost_in_window != null;
const windowDays = (snapshot) => snapshot?.thresholds?.loss_window_days || 30;
// The client's Critical / At Risk limits the snapshot was built with.
const riskBands = (snapshot) => snapshot?.thresholds?.risk_bands || DEFAULT_RISK_BANDS;

/** A lane's items, today first then by money (the backend's order within a severity). */
export const laneItems = (items, signal) => {
  const rows = (items || []).filter((item) => item.signal === signal);
  return [...rows.filter((item) => item.severity === "today"), ...rows.filter((item) => item.severity !== "today")];
};

/** Counts and money for a lane header. */
export const laneSummary = (items) => ({
  count: items.length,
  today: items.filter((item) => item.severity === "today").length,
  week: items.filter((item) => item.severity !== "today").length,
  perDay: items.reduce((sum, item) => sum + (item.impact?.per_day || 0), 0),
  lostInWindow: items.reduce((sum, item) => sum + (item.impact?.lost_in_window || 0), 0),
  hasWindowLoss: items.length > 0 && items.every(hasWindowLoss),
  orders: items.reduce((sum, item) => sum + (item.impact?.orders || 0), 0),
  value: items.reduce((sum, item) => sum + (item.impact?.value || 0), 0),
});

/** The line under a lane title: what the lane's items add up to. */
export const laneCaption = (signal, summary) => {
  if (!summary.count) return "Nothing needs attention";
  if (signal === "orders") return `${fmtUnits(summary.orders)} orders past normal ship time · ${fmtMoney(summary.value)}`;
  if (signal === "po") return `${fmtUnits(summary.count)} late POs refilling ${fmtCompactMoney(summary.perDay)}/day of sales`;
  return `${fmtCompactMoney(summary.perDay)}/day of top-seller sales at risk`;
};

/** "$48,690" (next 30 days) for stock items, "$2,083/day" for POs, "$4,449" for order groups. */
export const impactText = (item) => {
  if (!item || !item.impact) return "";
  if (item.signal === "orders") return fmtMoney(item.impact.value);
  if (item.signal === "stock" && hasWindowLoss(item)) return fmtMoney(item.impact.lost_in_window);
  return `${fmtMoney(item.impact.per_day)}/day`;
};

export const impactCaption = (item, snapshot) => {
  if (!item || !item.impact) return "";
  if (item.signal === "orders") return `${fmtUnits(item.impact.orders)} orders`;
  if (item.signal === "po") return "of sales it would refill";
  if (hasWindowLoss(item)) return `at risk, next ${windowDays(snapshot)} days · ${fmtMoney(item.impact.per_day)}/day`;
  return "of sales at risk";
};

/**
 * The banner: one sentence on the state of the business today, and its tone.
 * "alert" when something must be done today, "watch" for this week only,
 * "clear" when nothing is flagged.
 */
export const bannerState = (snapshot) => {
  const items = snapshot?.items || [];
  const today = items.filter((item) => item.severity === "today");
  const week = items.length - today.length;
  if (today.length) {
    const count = (signal) => today.filter((item) => item.signal === signal).length;
    const parts = [
      [count("stock"), "product", "products"],
      [count("orders"), "order issue", "order issues"],
      [count("po"), "PO", "POs"],
    ]
      .filter(([n]) => n > 0)
      .map(([n, one, many]) => `${fmtUnits(n)} ${n === 1 ? one : many}`);
    return {
      tone: "alert",
      eyebrow: "Needs your attention today",
      title: `${fmtUnits(today.length)} action${today.length === 1 ? "" : "s"} today: ${parts.join(", ")}`,
    };
  }
  if (week) {
    return { tone: "watch", eyebrow: "Nothing urgent today", title: `${fmtUnits(week)} thing${week === 1 ? "" : "s"} to handle this week` };
  }
  return { tone: "clear", eyebrow: "All clear", title: "Nothing needs your attention right now" };
};

// Where each section's "open the full analysis" link goes.
export const SECTION_LINKS = {
  stock: { path: "/purchase-order-analysis", label: "Purchase Order Analysis" },
  orders: { path: "/late-ship", label: "Order Exceptions" },
  po: { path: "/purchase-order-analysis", label: "Purchase Order Analysis" },
};

/**
 * One summary tile per section: the number that matters, what it means, and
 * the single biggest item, so the tiles alone tell the story.
 */
export const sectionTiles = (snapshot) => {
  const items = snapshot?.items || [];
  const lateDays = snapshot?.thresholds?.late_after_days;
  return SIGNALS.map((signal) => {
    const rows = laneItems(items, signal.key);
    const summary = laneSummary(rows);
    const top = rows[0] || null;
    const tile = { key: signal.key, label: signal.label, summary, top, urgent: summary.today > 0 };
    if (signal.key === "orders") {
      return {
        ...tile,
        value: fmtUnits(summary.orders),
        unit: summary.orders === 1 ? "order late" : "orders late",
        caption: summary.orders ? `${fmtMoney(summary.value)} open longer than ${lateDays} days` : `None open longer than ${lateDays} days`,
      };
    }
    if (signal.key === "po") {
      return {
        ...tile,
        value: fmtUnits(summary.count),
        unit: summary.count === 1 ? "late PO" : "late POs",
        caption: summary.count ? `Refilling ${fmtCompactMoney(summary.perDay)}/day of at-risk sales` : "None holding up stock",
      };
    }
    const products = `${fmtUnits(summary.count)} top-selling product${summary.count === 1 ? "" : "s"} running out`;
    if (summary.hasWindowLoss) {
      return {
        ...tile,
        value: fmtCompactMoney(summary.lostInWindow),
        unit: `sales at risk, next ${windowDays(snapshot)} days`,
        caption: `${products} · ${fmtCompactMoney(summary.perDay)}/day`,
      };
    }
    return {
      ...tile,
      value: summary.count ? fmtCompactMoney(summary.perDay) : "$0",
      unit: "/day at risk",
      caption: summary.count ? products : "No top sellers running out",
    };
  });
};

/** The sentence under a section title. */
export const sectionSubtitle = (signal, summary, snapshot) => {
  if (!summary.count) return "Nothing here needs attention.";
  const split = summary.week ? ` (${fmtUnits(summary.today)} today, ${fmtUnits(summary.week)} this week)` : "";
  if (signal === "orders") {
    return `${fmtUnits(summary.orders)} customer orders have been open longer than the usual ${snapshot?.thresholds?.late_after_days} days to ship — ${fmtMoney(summary.value)} in order value${split}.`;
  }
  if (signal === "po") {
    return `${fmtUnits(summary.count)} overdue POs carry stock that is running out — together they would refill ${fmtMoney(summary.perDay)}/day of sales${split}.`;
  }
  if (summary.hasWindowLoss) {
    return `${fmtUnits(summary.count)} top-selling products run out within ${riskBands(snapshot).at_risk} days with nothing arriving in time — ${fmtMoney(summary.lostInWindow)} of sales lost over the next ${windowDays(snapshot)} days if nothing changes (${fmtMoney(summary.perDay)}/day)${split}.`;
  }
  return `${fmtUnits(summary.count)} top-selling products run out within ${riskBands(snapshot).at_risk} days with nothing arriving in time — ${fmtMoney(summary.perDay)}/day of sales at risk${split}.`;
};

const MINUTE = 60 * 1000;

/** "just now", "6 min ago", "3 h ago", "2 days ago". */
export const timeAgo = (iso, now = Date.now()) => {
  if (!iso) return "—";
  const elapsed = Math.max(0, now - new Date(iso).getTime());
  if (elapsed < MINUTE) return "just now";
  if (elapsed < 60 * MINUTE) return `${Math.floor(elapsed / MINUTE)} min ago`;
  if (elapsed < 48 * 60 * MINUTE) return `${Math.floor(elapsed / (60 * MINUTE))} h ago`;
  return `${Math.floor(elapsed / (24 * 60 * MINUTE))} days ago`;
};

export const fmtDay = (value) => {
  if (!value) return "—";
  const date = new Date(String(value).length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
};

export const fmtTime = (value) =>
  value ? new Date(value).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "—";

/** "New" for items first seen in the last day, else "Since Oct 18". */
export const ageLabel = (item) => (item.is_new ? "New" : `Since ${fmtDay(item.first_seen_at)}`);

/**
 * One line per thing the Command Center tracked but deliberately did not list,
 * tagged with the lane it belongs under, so a short page never hides how much
 * sits below it. Lines with nothing in them are dropped.
 */
export const foldedLines = (snapshot) => {
  const folded = snapshot?.folded;
  if (!folded) return [];
  const floor = fmtMoney(snapshot.thresholds?.floor_per_day);
  const lines = [
    folded.small_products?.count && {
      key: "small_products",
      lane: "stock",
      text:
        folded.small_products.lost_in_window != null
          ? `${fmtUnits(folded.small_products.count)} more products running low, each under ${floor}/day (${fmtMoney(folded.small_products.lost_in_window)} at risk over the next ${windowDays(snapshot)} days together, ${fmtMoney(folded.small_products.per_day)}/day)`
          : `${fmtUnits(folded.small_products.count)} more products running low, each under ${floor}/day (${fmtMoney(folded.small_products.per_day)}/day together)`,
      path: "/purchase-order-analysis",
    },
    folded.long_stockouts?.count && {
      key: "long_stockouts",
      lane: "stock",
      text: `${fmtUnits(folded.long_stockouts.count)} SKUs out of stock all month (sold ${fmtMoney(folded.long_stockouts.per_day)}/day before)`,
      path: "/inventory-risk",
    },
    folded.covered_skus && {
      key: "covered_skus",
      lane: "stock",
      text: `${fmtUnits(folded.covered_skus)} low SKUs already covered by a PO arriving in time`,
      path: "/purchase-order-analysis",
    },
    folded.preorders_on_plan?.count && {
      key: "preorders_on_plan",
      lane: "orders",
      text: `${fmtUnits(folded.preorders_on_plan.count)} preorders waiting on a scheduled PO (on plan)`,
      path: "/late-ship",
    },
    folded.stale_orders?.count && {
      key: "stale_orders",
      lane: "orders",
      text: `${fmtUnits(folded.stale_orders.count)} orders open more than ${folded.stale_orders.older_than_days} days (${fmtMoney(folded.stale_orders.value)}) — likely stale statuses`,
      path: "/late-ship",
    },
    folded.small_pos?.count && {
      key: "small_pos",
      lane: "po",
      text: `${fmtUnits(folded.small_pos.count)} more late POs refilling smaller products`,
      path: "/purchase-order-analysis",
    },
    folded.late_pos_stock_ok && {
      key: "late_pos_stock_ok",
      lane: "po",
      text: `${fmtUnits(folded.late_pos_stock_ok)} late POs whose stock is not at risk`,
      path: "/purchase-order-analysis",
    },
  ];
  return lines.filter(Boolean);
};

/** Plain-language rules footer, built from the thresholds the snapshot applied. */
export const rulesText = (snapshot) => {
  const t = snapshot?.thresholds;
  if (!t) return "";
  const warehouses = (snapshot.scope?.warehouses || []).map((w) => w.name).join(", ") || "none";
  const lateness =
    t.late_after_source === "ship_time_p95"
      ? `your 95th-percentile ship time is ${t.ship_time_p95_hours} h`
      : "standard, too few shipped orders to measure yours";
  const bands = riskBands(snapshot);
  return [
    `Stock uses Inventory Risk's buckets across your warehouses: out of stock or Critical (≤${bands.critical} days of cover) is Act today, At Risk (≤${bands.at_risk} days) is This week, unless a PO lands before the stock-out (Purchase Order Analysis coverage).`,
    t.loss_window_days
      ? `Sales at risk over the next ${t.loss_window_days} days = each SKU's average daily sales over the last 30 days × the days it is out of stock in that window, from its stock-out until a PO with a date lands (or the window's end when no dated PO is on order).`
      : null,
    `Products and POs are listed when they put at least ${fmtMoney(t.floor_per_day)}/day at risk (0.5% of your ${fmtMoney(t.daily_revenue)}/day sales).`,
    `Orders are late after ${t.late_after_days} days open (${lateness}).`,
    `Warehouses: ${warehouses}${snapshot.scope?.source === "configured" ? " (your analytics warehouses)" : " (all holding stock)"}.`,
  ]
    .filter(Boolean)
    .join(" ");
};

export const commandCenterErrorMessage = (err) => {
  const data = err?.response?.data;
  return data?.message || err?.message || "The Command Center could not be loaded.";
};
